import * as React from "react";

export interface TranslogicaPreviewViewerProps {
    output: string;
}

// --- Typen ---

interface TAddress {
    name?: string;
    street?: string;
    zipCode?: string;
    city?: string;
    countryISO?: string;
    interfaceID?: string;
    interfaceIDReference?: string;
}

interface TSendingPosition {
    no: string;
    content?: string;
    quantity?: string;
    unit?: string;
    weight?: string;
    length?: string;
    width?: string;
    height?: string;
    deliveryNoteNumber?: string;
    customerReferenceNo?: string;
    sendingText?: string;
    articleNo?: string;
    adrUnNos?: string[];
}

interface TSendingCategory {
    id: string;
}

interface TSending {
    loadingDate?: string;
    loadingFromTime?: string;
    loadingToTime?: string;
    unloadingDate?: string;
    unloadingFromTime?: string;
    unloadingToTime?: string;
    pickUpNo?: string;
    unloadingReferenceNo?: string;
    loadingMeter?: string;
    deliveryNoteNumber?: string;
    loadingPlaceDriverBriefing?: string;
    unloadingPlaceDriverBriefing?: string;
    externalSendingID?: string;
    externalSendingNo?: string;
    loadingAddress?: TAddress;
    unloadingAddress?: TAddress;
    receivingForwarderAddress?: TAddress;
    positions: TSendingPosition[];
    categories?: TSendingCategory[];
    extraFields: { key: string; value: string }[];
}

interface TOrder {
    action?: string;
    customerReferenceNo?: string;
    externalOrderID?: string;
    customerNo?: string;
    billingCustomerNo?: string;
    sendings: TSending[];
}

interface TTranslogicaOrder {
    dateTime?: string;
    from?: string;
    order: TOrder;
}

// --- XML Parsing ---

function t(el: Element | Document, tag: string): string {
    return el.querySelector(tag)?.textContent?.trim() ?? "";
}

function parseAddress(el: Element | null): TAddress | undefined {
    if (!el) return undefined;
    return {
        name:                 t(el, "Name")                 || undefined,
        street:               t(el, "Street")               || undefined,
        zipCode:              t(el, "ZipCode")              || undefined,
        city:                 t(el, "City")                 || undefined,
        countryISO:           t(el, "CountryISO")           || undefined,
        interfaceID:          t(el, "InterfaceID")          || undefined,
        interfaceIDReference: t(el, "InterfaceIDReference") || undefined,
    };
}

const EXTRA_FIELD_TAGS = [
    "NoteField0","NoteField1","NoteField2","NoteField3","NoteField4",
    "Field0","Field1","Field2","Field3","Field4","Field5","Field6","Field7",
    "FieldDecimal0","FieldDecimal1","FieldDecimal9",
    "FieldBool0","FieldBool1",
];

function parseSending(el: Element): TSending {
    const extraFields: { key: string; value: string }[] = [];
    EXTRA_FIELD_TAGS.forEach(tag => {
        const v = t(el, tag);
        if (v) extraFields.push({ key: tag, value: v });
    });

    const positions = Array.from(el.querySelectorAll("SendingPositions > SendingPosition")).map(pos => ({
        no:                  t(pos, "SendingPositionNo"),
        content:             t(pos, "Content")             || undefined,
        quantity:            t(pos, "Quantity")            || undefined,
        unit:                t(pos, "Unit")                || undefined,
        weight:              t(pos, "Weight")              || undefined,
        length:              t(pos, "Length")              || undefined,
        width:               t(pos, "Width")               || undefined,
        height:              t(pos, "Height")              || undefined,
        deliveryNoteNumber:  t(pos, "DeliveryNoteNumber")  || undefined,
        customerReferenceNo: t(pos, "CustomerReferenceNo") || undefined,
        sendingText:         t(pos, "SendingText")         || undefined,
        articleNo:           t(pos, "Article > ArticleNo") || undefined,
        adrUnNos: Array.from(pos.querySelectorAll("SendingPositionADR > UnNo"))
                       .map(n => n.textContent?.trim() ?? "").filter(Boolean),
    } as TSendingPosition));

    const categories = Array.from(el.querySelectorAll("SendingCategories > SendingCategory")).map(c => ({
        id: t(c, "SendingCategoryID"),
    }));

    return {
        loadingDate:                 t(el, "LoadingDate")                 || undefined,
        loadingFromTime:             t(el, "LoadingFromTime")             || undefined,
        loadingToTime:               t(el, "LoadingToTime")               || undefined,
        unloadingDate:               t(el, "UnloadingDate")               || undefined,
        unloadingFromTime:           t(el, "UnloadingFromTime")           || undefined,
        unloadingToTime:             t(el, "UnloadingToTime")             || undefined,
        pickUpNo:                    t(el, "PickUpNo")                    || undefined,
        unloadingReferenceNo:        t(el, "UnloadingReferenceNo")        || undefined,
        loadingMeter:                t(el, "LoadingMeter")                || undefined,
        deliveryNoteNumber:          t(el, "DeliveryNoteNumber")          || undefined,
        loadingPlaceDriverBriefing:  t(el, "LoadingPlaceDriverBriefing")  || undefined,
        unloadingPlaceDriverBriefing: t(el, "UnloadingPlaceDriverBriefing") || undefined,
        externalSendingID:           t(el, "ExternalSendingID")           || undefined,
        externalSendingNo:           t(el, "ExternalSendingNo")           || undefined,
        loadingAddress:   parseAddress(el.querySelector("LoadingAddress")),
        unloadingAddress: parseAddress(el.querySelector("UnloadingAddress")),
        receivingForwarderAddress: parseAddress(el.querySelector("ReceivingForwarderAddress")),
        positions,
        categories: categories.length ? categories : undefined,
        extraFields,
    };
}

function parseTranslogicaXml(xml: string): TTranslogicaOrder | null {
    try {
        const doc = new DOMParser().parseFromString(xml, "application/xml");
        if (doc.querySelector("parseerror")) return null;
        if (!doc.querySelector("TranslogicaOrder")) return null;
        const ord = doc.querySelector("Order");
        if (!ord) return null;
        return {
            dateTime: t(doc, "Header > DateTime") || undefined,
            from:     t(doc, "Header > From")     || undefined,
            order: {
                action:             t(ord, "Action")                        || undefined,
                customerReferenceNo: t(ord, "CustomerReferenceNo")         || undefined,
                externalOrderID:     t(ord, "ExternalOrderID")             || undefined,
                customerNo:          t(ord, "CustomerAddress > CustomerNo") || undefined,
                billingCustomerNo:   t(ord, "BillingToAddress > CustomerNo") || undefined,
                sendings: Array.from(ord.querySelectorAll("Sendings > Sending")).map(parseSending),
            },
        };
    } catch { return null; }
}

export function parseTranslogicaData(output: string): TTranslogicaOrder | null {
    if (!output) return null;
    const trimmed = output.trim();
    if (!trimmed.startsWith("<")) return null;
    return parseTranslogicaXml(trimmed);
}

// --- Hilfsfunktionen ---

function fmtDT(iso?: string): string {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        const dd  = String(d.getDate()).padStart(2, "0");
        const mm  = String(d.getMonth() + 1).padStart(2, "0");
        const yyyy = d.getFullYear();
        const hh  = String(d.getHours()).padStart(2, "0");
        const min = String(d.getMinutes()).padStart(2, "0");
        return `${dd}.${mm}.${yyyy} ${hh}:${min}`;
    } catch { return iso; }
}

// --- Style ---

const amber  = "#e8a000";
const navy   = "#1a3a5c";
const S = {
    root: {
        fontFamily: "Segoe UI, Tahoma, Arial, sans-serif",
        fontSize: 11,
        background: "#e8e8e8",
        padding: 6,
        overflowY: "auto" as const,
        height: "100%",
        boxSizing: "border-box" as const,
    },
    orderHeader: {
        background: amber,
        color: "#fff",
        fontWeight: "bold" as const,
        padding: "3px 8px",
        fontSize: 12,
        marginBottom: 4,
        display: "flex",
        gap: 12,
        alignItems: "center",
    },
    sendingHeader: {
        background: navy,
        color: "#fff",
        fontWeight: "bold" as const,
        padding: "2px 8px",
        fontSize: 11,
        margin: "6px 0 3px",
    },
    sectionTitle: {
        background: "#d6e0f0",
        border: "1px solid #a0aec0",
        padding: "1px 6px",
        fontWeight: "bold" as const,
        fontSize: 10,
        color: "#333",
        margin: "3px 0 2px",
    },
    twoCol: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 6,
        marginBottom: 4,
    },
    addrBox: {
        background: "#fff",
        border: "1px solid #bbb",
        padding: 4,
    },
    addrTitle: {
        background: amber,
        color: "#fff",
        fontWeight: "bold" as const,
        fontSize: 10,
        padding: "1px 5px",
        marginBottom: 3,
    },
    row: {
        display: "flex",
        flexWrap: "wrap" as const,
        alignItems: "center",
        gap: 4,
        marginBottom: 2,
    },
    lbl: {
        fontSize: 10,
        color: "#555",
        whiteSpace: "nowrap" as const,
    },
    lblBold: {
        fontSize: 10,
        fontWeight: "bold" as const,
        color: "#333",
        whiteSpace: "nowrap" as const,
    },
    inp: (w?: number, highlight?: boolean): React.CSSProperties => ({
        border: "1px solid #aaa",
        background: highlight ? "#fff8e0" : "#fff",
        padding: "1px 4px",
        minHeight: 17,
        fontSize: 11,
        display: "inline-flex",
        alignItems: "center",
        whiteSpace: "nowrap" as const,
        overflow: "hidden",
        minWidth: w ?? 60,
        width: w ?? undefined,
    }),
    inpWide: {
        border: "1px solid #aaa",
        background: "#fff",
        padding: "1px 4px",
        minHeight: 17,
        fontSize: 11,
        flex: 1,
        whiteSpace: "pre-wrap" as const,
        wordBreak: "break-word" as const,
    },
    th: {
        border: "1px solid #888",
        background: amber,
        color: "#fff",
        padding: "1px 5px",
        fontWeight: "bold" as const,
        fontSize: 10,
        whiteSpace: "nowrap" as const,
        textAlign: "left" as const,
    },
    td: {
        border: "1px solid #ccc",
        padding: "1px 5px",
        fontSize: 11,
        whiteSpace: "nowrap" as const,
    },
    tdWrap: {
        border: "1px solid #ccc",
        padding: "1px 5px",
        fontSize: 11,
        whiteSpace: "pre-wrap" as const,
        maxWidth: 300,
    },
    divider: { borderTop: "1px solid #ccc", margin: "3px 0" },
};

function F({ lbl, v, w, hi }: { lbl: string; v?: string; w?: number; hi?: boolean }) {
    if (!v) return null;
    return (
        <>
            <span style={S.lbl}>{lbl}</span>
            <div style={S.inp(w, hi)}>{v}</div>
        </>
    );
}

function AddressBox({ title, addr }: { title: string; addr?: TAddress }) {
    if (!addr) return null;
    return (
        <div style={S.addrBox}>
            <div style={S.addrTitle}>{title}</div>
            <div style={S.row}>
                {addr.name && <div style={{ ...S.inp(), fontWeight: "bold", minWidth: 160 }}>{addr.name}</div>}
                {addr.countryISO && <div style={S.inp(24)}>{addr.countryISO}</div>}
                {addr.zipCode   && <div style={S.inp(50)}>{addr.zipCode}</div>}
                {addr.city      && <div style={S.inp(120)}>{addr.city}</div>}
            </div>
            {addr.street && (
                <div style={S.row}>
                    <span style={S.lbl}>Straße:</span>
                    <div style={{ ...S.inp(), minWidth: 200 }}>{addr.street}</div>
                </div>
            )}
            {(addr.interfaceIDReference || addr.interfaceID) && (
                <div style={S.row}>
                    <span style={S.lbl}>Ref.:</span>
                    <div style={S.inp()}>{addr.interfaceIDReference ?? addr.interfaceID}</div>
                </div>
            )}
        </div>
    );
}

function SendingView({ s, idx }: { s: TSending; idx: number }) {
    const [extrasOpen, setExtrasOpen] = React.useState(false);

    return (
        <div style={{ marginBottom: 6 }}>
            <div style={S.sendingHeader}>
                Sendung {idx + 1}
                {s.externalSendingID ? ` — ID: ${s.externalSendingID}` : ""}
            </div>

            {/* Lade- und Entladedaten */}
            <div style={S.row}>
                <span style={S.lblBold}>Laden:</span>
                <span style={S.lbl}>von</span>
                <div style={S.inp(115, true)}>{fmtDT(s.loadingFromTime)}</div>
                <span style={S.lbl}>bis</span>
                <div style={S.inp(115, true)}>{fmtDT(s.loadingToTime)}</div>
                {s.pickUpNo && <>
                    <span style={S.lbl}>Lade-Ref.:</span>
                    <div style={S.inp()}>{s.pickUpNo}</div>
                </>}
                {s.loadingMeter && <>
                    <span style={S.lbl}>LDM:</span>
                    <div style={S.inp(50)}>{s.loadingMeter}</div>
                </>}
            </div>
            <div style={S.row}>
                <span style={S.lblBold}>Entladen:</span>
                <span style={S.lbl}>von</span>
                <div style={S.inp(115, true)}>{fmtDT(s.unloadingFromTime)}</div>
                <span style={S.lbl}>bis</span>
                <div style={S.inp(115, true)}>{fmtDT(s.unloadingToTime)}</div>
                {s.unloadingReferenceNo && <>
                    <span style={S.lbl}>Entlade-Ref.:</span>
                    <div style={S.inp()}>{s.unloadingReferenceNo}</div>
                </>}
                {s.deliveryNoteNumber && <>
                    <span style={S.lbl}>Lieferschein-Nr.:</span>
                    <div style={S.inp()}>{s.deliveryNoteNumber}</div>
                </>}
            </div>

            {/* Adressen */}
            <div style={S.twoCol}>
                <div>
                    <div style={S.sectionTitle}>LADESTELLE</div>
                    <AddressBox title="Versender" addr={s.loadingAddress} />
                    {s.loadingPlaceDriverBriefing && (
                        <div style={S.row}>
                            <span style={S.lbl}>Fahrerweisung:</span>
                            <div style={S.inpWide}>{s.loadingPlaceDriverBriefing}</div>
                        </div>
                    )}
                </div>
                <div>
                    <div style={S.sectionTitle}>ENTLADESTELLE</div>
                    <AddressBox title="Empfänger" addr={s.unloadingAddress} />
                    {s.receivingForwarderAddress && (
                        <AddressBox title="Weiterl. Spediteur" addr={s.receivingForwarderAddress} />
                    )}
                    {s.unloadingPlaceDriverBriefing && (
                        <div style={S.row}>
                            <span style={S.lbl}>Fahrerweisung:</span>
                            <div style={S.inpWide}>{s.unloadingPlaceDriverBriefing}</div>
                        </div>
                    )}
                </div>
            </div>

            {/* Positionen */}
            {s.positions.length > 0 && (
                <>
                    <div style={S.sectionTitle}>Waren</div>
                    <div style={{ overflowX: "auto" }}>
                        <table style={{ borderCollapse: "collapse", fontSize: 11, background: "#fff", width: "100%" }}>
                            <thead>
                                <tr>
                                    {["Nr.", "Sendungstext / Inhalt", "Anz.", "Einheit",
                                      "Gewicht (kg)", "LDM", "L (cm)", "B (cm)", "H (cm)",
                                      "ADR UN-Nr.", "Artikel-Nr.", "Lieferschein-Nr.", "Ref."].map(h => (
                                        <th key={h} style={S.th}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {s.positions.map((p, i) => (
                                    <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#fafafa" }}>
                                        <td style={S.td}>{p.no}</td>
                                        <td style={S.tdWrap}>{p.sendingText ?? p.content ?? ""}</td>
                                        <td style={S.td}>{p.quantity}</td>
                                        <td style={S.td}>{p.unit}</td>
                                        <td style={S.td}>{p.weight}</td>
                                        <td style={S.td}></td>
                                        <td style={S.td}>{p.length}</td>
                                        <td style={S.td}>{p.width}</td>
                                        <td style={S.td}>{p.height}</td>
                                        <td style={S.td}>{p.adrUnNos?.join(", ")}</td>
                                        <td style={S.td}>{p.articleNo}</td>
                                        <td style={S.td}>{p.deliveryNoteNumber}</td>
                                        <td style={S.td}>{p.customerReferenceNo}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {/* Extra-Felder & Kategorien (collapsible) */}
            {(s.extraFields.length > 0 || s.categories?.length) && (
                <>
                    <div
                        onClick={() => setExtrasOpen(o => !o)}
                        style={{ ...S.divider, cursor: "pointer", userSelect: "none",
                            display: "flex", alignItems: "center", gap: 4 }}
                    >
                        <span style={{ fontSize: 10, color: "#555", background: "#e8e8e8",
                            padding: "0 4px", marginTop: -7, position: "relative" }}>
                            {extrasOpen ? "▾" : "▸"} Zusatzfelder / Kategorien
                        </span>
                    </div>
                    {extrasOpen && (
                        <div style={{ background: "#fff", border: "1px solid #ccc", padding: 4 }}>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: "3px 12px" }}>
                                {s.extraFields.map(f => (
                                    <div key={f.key} style={S.row}>
                                        <span style={S.lbl}>{f.key}:</span>
                                        <div style={S.inp()}>{f.value}</div>
                                    </div>
                                ))}
                            </div>
                            {s.categories?.length && (
                                <div style={{ marginTop: 4, fontSize: 10, color: "#555" }}>
                                    Kategorien: {s.categories.map(c => c.id).join(", ")}
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

export function TranslogicaPreviewViewer({ output }: TranslogicaPreviewViewerProps) {
    const data = React.useMemo(() => parseTranslogicaData(output), [output]);

    if (!data) {
        return (
            <div style={{ color: "#888", fontStyle: "italic", padding: 16, fontSize: 12,
                fontFamily: "Segoe UI, Tahoma, sans-serif" }}>
                Kein Translogica-XML erkannt. Erwartet wird ein XML mit &lt;TranslogicaOrder&gt;-Root.
            </div>
        );
    }

    const ord = data.order;
    return (
        <div style={S.root}>
            {/* Auftrags-Header */}
            <div style={S.orderHeader}>
                <span>Auftrag</span>
                {ord.externalOrderID     && <span>AUFTRAGS-NR. {ord.externalOrderID}</span>}
                {ord.customerReferenceNo && <span>Kunden-Ref.: {ord.customerReferenceNo}</span>}
                {ord.action              && <span style={{ fontSize: 10, opacity: 0.85 }}>Action: {ord.action}</span>}
            </div>
            {data.dateTime && (
                <div style={{ fontSize: 10, color: "#666", marginBottom: 4 }}>
                    Exportiert: {fmtDT(data.dateTime)} von {data.from ?? ""}
                </div>
            )}

            {/* Auftraggeber + Rechnung an */}
            {(ord.customerNo || ord.billingCustomerNo) && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 6 }}>
                    <div style={{ background: "#fff", border: "1px solid #bbb", padding: 4 }}>
                        <div style={{ ...S.addrTitle, marginBottom: 3 }}>AUFTRAGGEBER</div>
                        <div style={S.row}>
                            <span style={S.lbl}>Kunden-Nr.:</span>
                            <div style={S.inp(80)}>{ord.customerNo ?? "—"}</div>
                        </div>
                    </div>
                    <div style={{ background: "#fff", border: "1px solid #bbb", padding: 4 }}>
                        <div style={{ ...S.addrTitle, marginBottom: 3 }}>RECHNUNG AN</div>
                        <div style={S.row}>
                            <span style={S.lbl}>Kunden-Nr.:</span>
                            <div style={S.inp(80)}>{ord.billingCustomerNo ?? "—"}</div>
                        </div>
                    </div>
                </div>
            )}

            {/* Sendungen */}
            {ord.sendings.map((s, i) => (
                <SendingView key={i} s={s} idx={i} />
            ))}
        </div>
    );
}
